import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

export function ToastContainer({toastMessages, bottom, left, right, removeToast, isMobile}) {
  return <div className="toast-container" style={{
    position: 'absolute',
    bottom,
    left,
    right,
    zIndex: 9000,
    width: isMobile ? 'calc(100% - 20px)' : '680px',
    maxWidth: isMobile ? '380px' : 'none',
    pointerEvents: 'none',
    textAlign: 'left',
  }}>
    {toastMessages.map((toast, index) => (
      <div
        key={toast.id}
        style={{
          marginBottom: '12px',
          width: '100%',
          position: 'relative',
          pointerEvents: 'auto',
        }}
      >
        {/* Wrapper div that doesn't get affected by ToastNotification's internal animations */}
        <div style={{
          animation: 'slideInToLeft 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards',
          animationFillMode: 'both',
        }}>                
          <ToastNotification
            subject={toast.subject}
            body={toast.body}
            icon={toast.icon}
            onClose={() => removeToast(toast.id)}
            isMobile={isMobile}
            stackIndex={index}
            totalToasts={toastMessages.length}
            insideContainer={true}
            lightTheme={true}
          />
        </div>
      </div>
    ))}
  </div>
}

export default function ToastNotification({ subject, body, icon, onClose, isMobile, stackIndex = 0, totalToasts = 1, insideContainer = false, insideDeviceContainer = false, hideClose = false, lightTheme = false }) {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  // Get the side panel icon from Redux config
  const sidePanelIconSrc = useSelector((state) => state.channel.sidePanelIconSrc);
  
  // Get side panel expanded state to adjust positioning
  const sidePanelExpanded = useSelector((state) => state.sidePanel.sidePanelExpanded);
  const windowWidth = useSelector((state) => state.sidePanel.windowWidth);
  const deviceType = useSelector((state) => state.device.type);
  
  // Keep desktop toasts centred on the phone. The phone sits between the fixed
  // left toolbar and the right-hand side panel, so its centre is offset from the
  // viewport centre by (toolbarWidth - panelWidth) / 2. LEFT_TOOLBAR_W mirrors
  // LEFT_TOOLBAR_WIDTH in App.js.
  const LEFT_TOOLBAR_W = 80;
  const devicePxWidth = deviceType === 'phone' ? 380 : 820;
  const employeePaneWidth = sidePanelExpanded
    ? Math.max(windowWidth - devicePxWidth - 200 - LEFT_TOOLBAR_W, 460)
    : 0;
  const horizontalOffset = (LEFT_TOOLBAR_W - employeePaneWidth) / 2;
  
  // Use side panel icon if available, otherwise fall back to 'work' icon
  const displayIconType = 'icon';
  const displayIcon = icon || sidePanelIconSrc || 'work';

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      onClose();
    }, 300); // Match animation duration
  };

  useEffect(() => {
    // Fade in animation
    setTimeout(() => setIsVisible(true), 10);

    // Auto-close after 10 seconds
    const timer = setTimeout(() => {
      handleClose();
    }, 10000);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Calculate offset for stacking
  // Newer toasts at top (stackIndex = totalToasts - 1), older ones pushed down and fade behind
  // stackIndex 0 = oldest, stackIndex totalToasts-1 = newest
  const isNewest = stackIndex === totalToasts - 1;
  const positionFromTop = totalToasts - 1 - stackIndex; // 0 for newest, increases for older
  
  // Stacking offsets - older toasts move down and scale smaller
  const offsetY = positionFromTop * 45; // Vertical offset: older toasts move down
  const desktopScale = 1 - (positionFromTop * 0.12); // Scale reduction: older toasts shrink significantly (12% per toast)
  const mobileScale = 1 - (positionFromTop * 0.08); // Scale reduction for mobile: older toasts shrink less (8% per toast)
  
  // Opacity calculation - much higher transparency for older toasts
  // First toast (newest): 100%, second: 25%, third: 8%, fourth+: 3%
  const desktopOpacity = isVisible && !isExiting ? Math.max(0.03, 1 - (positionFromTop * 0.75)) : 0;
  const baseOpacity = lightTheme ? 0.8 : 1; // 80% opacity for light theme
  
  // For mobile inside container: newest toast is fully opaque, older ones fade significantly
  const mobileContainerOpacity = isVisible && !isExiting ? Math.max(0.3, 1 - (positionFromTop * 0.5)) : 0;
  
  const opacity = insideDeviceContainer 
    ? desktopOpacity 
    : insideContainer 
      ? mobileContainerOpacity 
      : (isVisible && !isExiting ? (isNewest ? baseOpacity : 0.85 * baseOpacity) : 0);

  // When inside device container (desktop toasts outside phone), use fixed positioning
  // but positioned below the centered device and shift right when side panel expands
  // When inside a container (like FooterCarousel), use relative positioning
  const positionStyle = insideDeviceContainer ? {
    position: 'fixed',
    bottom: '40px', // Position 40px from bottom (moved down by 10px from 50px)
    left: `calc(50% + ${horizontalOffset}px)`, // Center horizontally and shift right when side panel expands
    width: '300px',
    maxWidth: 'calc(100vw - 48px)',
  } : insideContainer ? {
    position: 'relative',
    width: '100%',
  } : {
    position: isMobile ? 'relative' : 'fixed',
    bottom: isMobile ? 'auto' : `${20 + offsetY}px`,
    left: isMobile ? 'auto' : '50%',
  };

  return (
    <>
      {/* Add keyframe animations for activity effects */}
      <style>{`
        @keyframes slideInToRight {
          0% {
            transform: translateX(-430px);
            opacity: 0;
          }
          60% {
            opacity: 1;
          }
          100% {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @keyframes slideInToLeft {
          0% {
            transform: translateX(430px);
            opacity: 0;
          }
          60% {
            opacity: 1;
          }
          100% {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @keyframes toast-shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }
        @keyframes toast-pulse-glow {
          0%, 100% { 
            box-shadow: 0 0 8px rgba(59, 130, 246, 0.2), 0 0 16px rgba(59, 130, 246, 0.1);
          }
          50% { 
            box-shadow: 0 0 16px rgba(59, 130, 246, 0.35), 0 0 24px rgba(59, 130, 246, 0.18);
          }
        }
        @keyframes toast-icon-pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
      `}</style>
      
      <div
        className="toast-notification"
        style={{
          ...positionStyle,
          transform: insideDeviceContainer
            ? `translateX(-50%) translateY(${offsetY}px) scale(${isVisible && !isExiting ? desktopScale : desktopScale * 0.95})` // Older toasts move down and scale smaller
            : insideContainer 
            ? `scale(${isVisible && !isExiting ? mobileScale : mobileScale * 0.95})`
            : isMobile
              ? `translateY(${offsetY}px) scale(${mobileScale}) translateY(${isVisible && !isExiting ? '0' : '20px'})`
              : `translateX(-50%) translateY(${isVisible && !isExiting ? '0' : '20px'}) scale(${desktopScale})`,
          opacity: opacity,
          transition: insideDeviceContainer 
            ? 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1), left 0.3s ease' // Smooth animation for stacking and side panel shift
            : 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          zIndex: insideDeviceContainer ? (200 + stackIndex) : (insideContainer ? 1 + stackIndex : (isMobile ? 10001 + stackIndex : 200 + stackIndex)), // Higher stackIndex = higher z-index (newer on top). Must be above SidePanel (z-index: 120)
          maxWidth: insideDeviceContainer ? '100%' : (insideContainer ? '100%' : (isMobile ? 'calc(100vw - 40px)' : '420px')),
          pointerEvents: 'auto',
        }}
      >
        {/* Desktop: Simple minimal text-based indicator */}
        {insideDeviceContainer ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '8px 12px',
              background: 'transparent',
              position: 'relative',
            }}
          >
            {/* Icon */}
            {displayIcon && (
              <div
                style={{
                  flexShrink: 0,
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0.9,
                }}
              >
                {displayIconType === 'image' ? (
                  <img 
                    src={displayIcon} 
                    alt="icon"
                    style={{ 
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                    }}
                  />
                ) : (
                  <span className="material-icons" style={{ 
                    fontSize: 20,
                    color: '#64748b',
                  }}>
                    {displayIcon}
                  </span>
                )}
              </div>
            )}

            {/* Text Content */}
            <div style={{ flex: 1, minWidth: 0 }}>
              {/* Subject/Header — single line, ellipsised if too long */}
              {subject && (
                <div style={{
                  fontSize: '16px',
                  color: '#ffffff',
                  fontWeight: 600,
                  lineHeight: '1.3',
                  marginBottom: body ? '4px' : '0',
                  fontFamily: 'Lato, -apple-system, BlinkMacSystemFont, sans-serif',
                  WebkitFontSmoothing: 'antialiased',
                  MozOsxFontSmoothing: 'grayscale',
                  textRendering: 'optimizeLegibility',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                  {subject}
                </div>
              )}

              {/* Body/Footer */}
              {body && (
                <div style={{
                  fontSize: '14px',
                  color: 'rgba(255, 255, 255, 0.85)',
                  fontWeight: 400,
                  lineHeight: '1.45',
                  fontFamily: 'Lato, -apple-system, BlinkMacSystemFont, sans-serif',
                  WebkitFontSmoothing: 'antialiased',
                  MozOsxFontSmoothing: 'grayscale',
                  textRendering: 'optimizeLegibility',
                }}>
                  {body}
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Mobile: Slim status-pill styling (Pega background activity) */
          <div
            style={{
              background: 'rgba(15, 23, 42, 0.78)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              borderRadius: '999px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.18)',
              padding: '6px 12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              position: 'relative',
              overflow: 'hidden',
              borderLeft: '2px solid rgba(96, 165, 250, 0.7)',
            }}
          >
            {/* Icon */}
            {displayIcon && (
              <div
                style={{
                  flexShrink: 0,
                  width: '16px',
                  height: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {displayIconType === 'image' ? (
                  <img
                    src={displayIcon}
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'contain', opacity: 0.85 }}
                  />
                ) : (
                  <span className="material-icons" style={{ fontSize: 14, color: '#94a3b8' }}>
                    {displayIcon}
                  </span>
                )}
              </div>
            )}

            {/* Content - single row, ellipsised */}
            <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: '6px', overflow: 'hidden' }}>
              {subject && (
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: '14px',
                    color: '#e2e8f0',
                    lineHeight: '1.2',
                    fontFamily: 'Lato, -apple-system, BlinkMacSystemFont, sans-serif',
                    flexShrink: 0,
                    maxWidth: '40%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {subject}
                </span>
              )}
              {body && (
                <span
                  style={{
                    fontSize: '14px',
                    color: '#94a3b8',
                    lineHeight: '1.2',
                    fontFamily: 'Lato, -apple-system, BlinkMacSystemFont, sans-serif',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                  }}
                  title={body}
                >
                  {body}
                </span>
              )}
            </div>

            {/* Close button - minimal */}
            {!hideClose && (
              <button
                onClick={handleClose}
                style={{
                  flexShrink: 0,
                  width: '16px',
                  height: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  opacity: 0.6,
                }}
                aria-label="Close notification"
              >
                <span className="material-icons" style={{ fontSize: 14, color: '#94a3b8' }}>close</span>
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
}
